import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";

import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectGroup, 
  SelectItem,
  SelectLabel,
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogFixUtil } from "@/components/ui/dialog-fix-util";
import { Loader2, Building, Search, Plus, Edit, Trash, Copy, MoreHorizontal, Home, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Association } from "@/models";
import { associationApi, PaginatedResponse } from "@/services/api/associationApi";
import { BulkUploadAssociations } from "@/components/associations/BulkUploadAssociations";
import { AssociationPropertiesBulkUploadModal } from "@/components/associations/AssociationPropertiesBulkUploadModal";
import { PaginationControls } from "@/components/shared/PaginationControls";
import { extractDataArray } from "@/utils/apiUtils";

const Associations = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentUser, userRoles, hasPermission, selectedRole, permissionsLoaded, getCurrentRoleIds } = useAuth();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("all");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit] = useState<number>(20);

  const [bulkUploadOpen, setBulkUploadOpen] = useState<boolean>(false);
  const [bulkPropertiesUploadOpen, setBulkPropertiesUploadOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [associationToDelete, setAssociationToDelete] = useState<Association | null>(null);

  const offset = (currentPage - 1) * limit;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType]);

  // Simple filtering logic for associations
  const getApiFilters = () => {
    const filters: any = {
      search: debouncedSearchTerm,
      type: filterType !== "all" ? filterType : undefined,
      limit,
      offset
    };

    return filters;
  };

  const { 
    data: response, 
    isLoading, 
    error 
  } = useQuery<PaginatedResponse<Association> | Association[]>({
    queryKey: ["associations", { 
      search: debouncedSearchTerm, 
      type: filterType !== "all" ? filterType : undefined, 
      limit, 
      offset,
      selectedRole: selectedRole?.type,
      entityId: selectedRole?.entityId
    }],
    queryFn: () => {
      const filters = getApiFilters();
      console.log("Associations API call with filters:", filters);
      
      return associationApi.list({
        queryKey: [
          "associations", 
          filters
        ]
      });
    },
    enabled: !!selectedRole && permissionsLoaded, // Only run query when we have a selected role AND permissions are loaded
    meta: {
      onError: (err: Error) => {
        toast({
          title: "Error fetching associations",
          description: err.message,
          variant: "destructive"
        });
      }
    }
  });

  const associations = extractDataArray<Association>(response);

  useEffect(() => {
    if (response && 'metadata' in response && response.metadata?.total_count !== undefined) {
      setTotalCount(response.metadata.total_count);
    } else if (Array.isArray(response)) {
      setTotalCount(response.length);
    }
  }, [response]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: number) => associationApi.delete(id),
    onSuccess: () => {
      toast({
        title: "Association deleted",
        description: "The association has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["associations"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting association",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleDeleteClick = (association: Association) => {
    setAssociationToDelete(association);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (associationToDelete) {
      deleteMutation.mutate(associationToDelete.id);
      setDeleteDialogOpen(false);
      setAssociationToDelete(null);
    }
  };

  const handleDuplicate = (association: Association) => {
    const { id: _, created_at: __, updated_at: ___, ...associationData } = association;
    navigate('/backoffice/associations/new', { state: { initialData: associationData } });
  };

  const handleOpenBulkUpload = () => {
    setBulkUploadOpen(true);
  };

  const handleOpenBulkPropertiesUpload = () => {
    setBulkPropertiesUploadOpen(true);
  };

  const handleBulkUploadOpenChange = (open: boolean) => {
    setBulkUploadOpen(open);
  };

  const handleBulkPropertiesUploadOpenChange = (open: boolean) => {
    setBulkPropertiesUploadOpen(open);
  };

  const handleRowClick = (association: Association, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('[data-actions-menu]')) {
      return;
    }
    if (canReadAssociations) {
      navigate(`/backoffice/associations/${association.id}/edit`);
    }
  };

  const handleViewDocuments = (association: Association) => {
    if (association.document_groups && association.document_groups.length > 0) {
      navigate(`/backoffice/documents`, {
        state: { document_group_ids: association.document_groups }
      });
    } else {
      toast({
        title: "No document groups",
        description: "This association has no document groups assigned.",
        variant: "default"
      });
    }
  };

  // Check if user can write associations (create, edit, delete, duplicate)
  const canWriteAssociations = (() => {
    if (!selectedRole || !permissionsLoaded) return false;

    if (selectedRole.type === 'platform') {
      return hasPermission(getCurrentRoleIds(), 'write', 'associations');
    } else if (selectedRole.type === 'management_company') {
      return hasPermission(getCurrentRoleIds(), 'write', 'associations');
    }
    return false;
  })();

  // Platform-only features: Add Multiple Associations, Bulk Upload Properties
  const isPlatformUser = selectedRole?.type === 'platform';

  // Platform users can always add associations, others need write permission
  const canShowAddAssociationDropdown = isPlatformUser || canWriteAssociations;

  // Check if user can read associations
  const canReadAssociations = (() => {
    if (!selectedRole || !permissionsLoaded) return false;
    
    if (selectedRole.type === 'platform') {
      return hasPermission(getCurrentRoleIds(), 'read', 'associations');
    } else if (selectedRole.type === 'management_company') {
      return hasPermission(getCurrentRoleIds(), 'read', 'associations');
    }
    return false;
  })();

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold flex items-center">
            <Building className="mr-2 h-8 w-8" />
            Associations
          </h1>
          <div className="flex space-x-2">
            {canShowAddAssociationDropdown && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Association
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canWriteAssociations && (
                    <DropdownMenuItem onClick={() => navigate("/backoffice/associations/new")}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Single Association
                    </DropdownMenuItem>
                  )}
                  {isPlatformUser && (
                    <DropdownMenuItem onClick={handleOpenBulkUpload}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Multiple Associations
                    </DropdownMenuItem>
                  )}
                  {isPlatformUser && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleOpenBulkPropertiesUpload}>
                        <Home className="mr-2 h-4 w-4" />
                        Bulk Upload Properties
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <Card className="p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search associations..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select 
              value={filterType} 
              onValueChange={setFilterType}
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Association Types</SelectLabel>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Condominium">Condominium</SelectItem>
                  <SelectItem value="HOA">Homeowners Association (HOA)</SelectItem>
                  <SelectItem value="COOP">Co-op (Cooperative)</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </Card>

        <div className="bg-white rounded-md shadow">
          {!permissionsLoaded || isLoading ? (
            <div className="py-8 flex justify-center items-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-sm text-gray-600">
                {!permissionsLoaded ? "Loading permissions..." : "Loading associations..."}
              </span>
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">Error loading associations</p>
            </div>
          ) : associations.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">
                {selectedRole?.type === 'management_company' 
                  ? "No associations found for your management company"
                  : selectedRole?.type === 'platform'
                  ? "No associations found"
                  : "No associations available"}
              </p>
              {canWriteAssociations && (
                <Button 
                  className="mt-4" 
                  variant="outline" 
                  onClick={() => navigate("/backoffice/associations/new")}
                >
                  Create your first association
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>External ID</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {associations.map((association) => (
                    <TableRow 
                      key={association.id} 
                      className="cursor-pointer"
                      onClick={(e) => handleRowClick(association, e)}
                    >
                      <TableCell className="font-medium">
                        {association.name}
                      </TableCell>
                      <TableCell>{association.association_type}</TableCell>
                      <TableCell>{association.email || '-'}</TableCell>
                      <TableCell>{association.phone || '-'}</TableCell>
                      <TableCell>{association.external_id || '-'}</TableCell>
                      <TableCell className="text-right" data-actions-menu>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            {canWriteAssociations && (
                              <DropdownMenuItem onClick={() => navigate(`/backoffice/associations/${association.id}/edit`)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {canWriteAssociations && (
                              <DropdownMenuItem onClick={() => handleDuplicate(association)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Duplicate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleViewDocuments(association)}>
                              <FileText className="mr-2 h-4 w-4" />
                              See documents
                            </DropdownMenuItem>
                            {canWriteAssociations && <DropdownMenuSeparator />}
                            {canWriteAssociations && (
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleDeleteClick(association)}
                              >
                                <Trash className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="flex justify-between items-center px-4 py-2 border-t">
                <div className="text-sm text-muted-foreground">
                  {totalCount > 0 ? (
                    `Showing ${offset + 1}-${Math.min(offset + associations.length, totalCount)} of ${totalCount} associations`
                  ) : (
                    "No associations found"
                  )}
                </div>
                
                {totalPages > 1 && (
                  <PaginationControls 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      <BulkUploadAssociations
        open={bulkUploadOpen}
        onOpenChange={handleBulkUploadOpenChange}
      />

      <AssociationPropertiesBulkUploadModal
        open={bulkPropertiesUploadOpen}
        onOpenChange={handleBulkPropertiesUploadOpenChange}
        managementCompanyId={currentUser?.management_company_id || 0}
      />

      <Dialog 
        open={deleteDialogOpen} 
        onOpenChange={setDeleteDialogOpen}
      >
        <DialogFixUtil open={deleteDialogOpen} />
        <DialogContent
          onPointerDownOutside={(e) => {
            if (e.target instanceof Node) {
              const target = e.target as HTMLElement;
              if (target.closest('[data-radix-popover-content]')) {
                e.preventDefault();
              }
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-popover-container]') || 
                target.closest('.tippy-box')) {
              e.preventDefault();
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete Association</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {associationToDelete?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BackofficeLayout>
  );
};

export default Associations;
